/**
 * Document and selfie capture (screens 13 and 14).
 *
 * Screen 14 is a selfie *video* with dictated text, so the interface exposes
 * both a still capture and a short recording. The web implementation uses a
 * file input with `capture`, which hands the OS camera UI to the user; the
 * Capacitor build will use the camera plugin with the same signatures.
 */
export interface CapturedMedia {
  blob: Blob
  mimeType: string
  /** Object URL for preview. The caller revokes it. */
  previewUrl: string
}

export interface CameraApi {
  isAvailable(): Promise<boolean>
  capturePhoto(options?: { facing?: 'user' | 'environment' }): Promise<CapturedMedia | null>
  captureVideo(options?: { maxSeconds?: number }): Promise<CapturedMedia | null>
}

function pickFile(accept: string, capture: string | null): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept
    if (capture) input.setAttribute('capture', capture)
    input.style.display = 'none'
    input.addEventListener('change', () => {
      resolve(input.files?.[0] ?? null)
      input.remove()
    })
    input.addEventListener('cancel', () => {
      resolve(null)
      input.remove()
    })
    document.body.append(input)
    input.click()
  })
}

async function wrap(file: File | null): Promise<CapturedMedia | null> {
  if (!file) return null
  return { blob: file, mimeType: file.type, previewUrl: URL.createObjectURL(file) }
}

export const webCamera: CameraApi = {
  async isAvailable() {
    return typeof document !== 'undefined'
  },
  async capturePhoto(options) {
    return wrap(await pickFile('image/*', options?.facing === 'user' ? 'user' : 'environment'))
  },
  async captureVideo() {
    return wrap(await pickFile('video/*', 'user'))
  },
}
